import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Company from '../../../../../models/company.js';
import type Workflow from '../../../../../models/workflow.js';
import definition from '@job-schemas/public-ledger/auto-payments/change-card.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { nsStripeServiceContext } from '../../../../../ns-services/contexts.js';
import { NSStripeService } from '../../../../../ns-services/ns-stripe-service.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { lambdaErrorCode } from '../../../../../helpers/lambda.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import { AUTOPAY_CODES, COMPANY_CARD_NOTICE, MANDATE_TEXT, cardDots } from '../../../../../helpers/auto-payments.js';
import type { ChangedCard } from '../../../../../helpers/auto-payments-api.js';
import {
  CHANGE_CARD_INTRO,
  SIGNATORY_CHANGE_REASON,
  cardSavedText,
  changeCardStage,
  changeDoneText,
  oldCardLine,
  type ChangeCardStage,
} from '../../../../../helpers/auto-payment-changes.js';
import { AutopayCardForm } from './card-form.js';

import '../../../../../ns-elements/ns-job-layout.js';

const logger = createLogger('ns-public-ledger-auto-payments-change-card');

/**
 * Change the automatic payments card (#25 phase 6): held by the signatory who started it from My Account. They enter the
 * new card (AutopayCardForm: a SetupIntent on the company's automatic payments customer, with the mandate text);
 * public-ledger/autopay/confirm-change-card holds it, never live, records the change (round 15: an account-signed public
 * ledger record naming who changed the card, with no card details) and completes the job `changed`. Once the account
 * signatories have signed it and it has run on the public ledger, the held card becomes the automatic payments card and the
 * old one is removed. Once the change has ended the card says how (its workflow's outcome). Round 15: never refused for
 * nodeStream's platform account; the record's fees wait for it after the card closes.
 */
@customElement(NSPublicLedgerAutoPaymentsChangeCard.is)
export default class NSPublicLedgerAutoPaymentsChangeCard extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-change-card';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-change-card'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  private __form = new AutopayCardForm(this, {
    stripe: () => this.__stripe,
    token: () => this._auth?.token,
    jobId: () => this._job?.id,
    held: (card) => this.__held(card),
    refused: async (err) => {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
      if (lambdaErrorCode(err) === AUTOPAY_CODES.signatoryChange) await this.__loadCompany();
    },
  });

  // Whether a signatory change is pending: the card change can't be signed under the account rule until it has finished
  @state()
  private __changePending = false;

  // The change's workflow, once the job is done: its outcome says how the change ended
  @state()
  private __workflow: (Workflow & { outcome?: string | null }) | null = null;

  async connectedCallback() {
    // Back from the bank's check of a new card (3-D Secure): busy from the start, so nothing can be pressed while it loads
    const returning = this.__form.returningFromBank();
    if (returning) {
      this._busy = true;
      this.loading('Saving the card');
    }
    try {
      await super.connectedCallback();
      const job = this._job;
      if (!job) return;
      await this.__loadCompany();
      await this.__loadWorkflow();
      const workflowId = job.workflow?.id;
      if (workflowId) this._subscriptions.subscribe(`workflow.${workflowId}.*`, () => void this.__loadWorkflow());
      if (returning && !this._completed && this._claimed) {
        try {
          await this.__form.returnFromBank();
        } catch (err) {
          this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
        }
      }
    } finally {
      if (returning) {
        this.loaded();
        this._busy = false;
      }
    }
  }

  private get __stage(): ChangeCardStage {
    return changeCardStage({
      completed: this._completed,
      claimed: this._claimed,
      changePending: this.__changePending,
      rowLoaded: this._rowLoaded,
    });
  }

  private async __loadCompany() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._db) return;
    try {
      const company = (await this._db.getById('company', companyId)) as Company | undefined;
      this.__changePending = company?.ledgerCred?.signatureRequirement?.awaitingUpdate === true;
    } catch (err) {
      logger.warn('Unable to read the company', err);
    }
  }

  // Read once the job is done, and again on the workflow's updates
  private async __loadWorkflow() {
    const workflowId = this._job?.workflow?.id;
    if (!workflowId || !this._db || !this._completed) return;
    try {
      this.__workflow =
        ((await this._db.getById('workflow', workflowId)) as typeof this.__workflow | undefined) ?? null;
    } catch (err) {
      logger.warn('Unable to read the change', err);
    }
  }

  private async __addCard() {
    if (this._busy) return;
    this._busy = true;
    this.clearError();
    this.loading('Starting the card form');
    try {
      await this.__form.start();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card form could not be started. Please try again.'));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  private async __saveCard() {
    if (this._busy) return;
    this._busy = true;
    this.clearError();
    this.loading('Saving the card');
    try {
      await this.__form.save();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  // The card is held and the change recorded: the server completes the job, whose workflow schedules the record
  private async __held(card: ChangedCard) {
    const job = this._job;
    if (!job) return;
    this._waiting = cardSavedText(cardDots(card));
    const updated = await this._db?.getById('job', job.id);
    if (updated) this._jobUpdated(updated as unknown as typeof job);
  }

  // "The new card": approved (round 4 follow-ups, small string 4)
  private __renderStage(): TemplateResult | typeof nothing {
    const live = cardDots(this._row);
    switch (this.__stage) {
      case 'DONE': {
        // No line while the change's workflow can't be read: the card just closes
        const done = changeDoneText('CARD', this.__workflow);
        return done
          ? html`
              <div class="job-panel">
                <p>${done}</p>
              </div>
            `
          : nothing;
      }
      case 'LOADING':
        return nothing;
      case 'CLAIM':
        return this._renderClaimNote('change the card');
      case 'LOCKED':
        return html`
          <div class="job-panel">
            <p>${SIGNATORY_CHANGE_REASON}.</p>
          </div>
        `;
      default: {
        return html`
          <div class="job-panel">
            <h3>The new card</h3>
            ${live
              ? html`
                  <p>${oldCardLine(live)}</p>
                `
              : nothing}
            <p><strong>${COMPANY_CARD_NOTICE}</strong></p>
            <p class="note">${MANDATE_TEXT}</p>
          </div>
          ${this.__form.clientSecret
            ? html`
                <div class="job-panel">
                  <slot name="stripe-payment-form"></slot>
                </div>
              `
            : nothing}
        `;
      }
    }
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">${CHANGE_CARD_INTRO}</p>
        ${this._renderWaiting() ?? this.__renderStage()}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    const stage = this.__stage;
    if (stage === 'LOADING') return nothing;
    if (!this._claimed) return this._renderClaimButton();
    const cancel = html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this._busy}
        @click=${() => this.outcome('cancel')}
      >
        Cancel
      </wa-button>
    `;
    if (stage !== 'CARD') return cancel;
    return html`
      ${cancel}
      ${this.__form.clientSecret
        ? html`
            <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__saveCard()}>
              Save card
            </wa-button>
          `
        : html`
            <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__addCard()}>
              Add card
            </wa-button>
          `}
    `;
  }
}
